import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import SEOHead from "../components/SEOHead";
import MapFacade from '../components/MapFacade';
import { Link } from 'react-router-dom';
import { Smartphone, Shield, Wrench, Phone, MapPin, Clock, Star, ChevronRight, Search, BadgeCheck, RefreshCw, MessageCircle, Camera, Store, Users, ExternalLink } from 'lucide-react';
// FIGYELEM: ezek SZÁNDÉKOSAN eager importok. Kipróbáltam a halasztott
// (IntersectionObserver + React.lazy) betöltést: ~15 kB-ot spórolt, DE ez az
// oldal PRERENDERELT – a halasztott szekciók kimaradtak a legyártott HTML-ből
// (vélemények, előtte/utána), tehát a crawler sem látta volna őket.
// Prerenderelt oldalon a hajtás alatti kód halasztása kontraproduktív.
import ReviewCarousel from '../components/ReviewCarousel';
import BeforeAfterSection from '../components/BeforeAfterSlider';
import TikTokSection from '../components/TikTokSection';
import SocialProofStrip from '../components/SocialProofStrip';
import StoreShowcase from '../components/StoreShowcase';
import VipScratch from '../components/VipScratch';
import { trackCallClick, trackMessengerClick, trackGoogleReviewsOpen, trackCtaClick, trackReviewsVisible, trackReviewsExpand } from '../utils/analytics';
import { MESSENGER_URL, PRIMARY_PHONE, STORES, isAnyStoreOpenNow } from '../constants/stores';
import { ELLENORZES_DB } from '../constants/ellenorzes';
import { fetchJsonCached } from '../utils/apiCache';
import { isPrerender } from '../utils/isPrerender';


// A Google-szinkron 'belvaros'/'tesco' kulcsokat ad – megjelenítési címkék.
// A két üzlet értékelései NEM keverednek: minden review a saját üzlet-címkéjével jelenik meg.
const STORE_LABELS = { belvaros: 'Belvárosi üzlet', tesco: 'Tesco üzletsor' };

const HomePage = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  // A `?source=` kiolvasása a VipScratch komponensben történik (a kód-prefixhez).

  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null); // valós Google-összesítő üzletenként

  // Zárva-állapot a hero másodlagos CTA-jához. A kezdőérték SZÁNDÉKOSAN `false`:
  // az oldal prerenderelt, és a build gépének órája szerinti állapot égne bele a
  // HTML-be. A tényleges állapot az első kliensoldali renderben áll be – így a
  // legyártott HTML és a hidratált DOM nem tér el.
  const [heroClosed, setHeroClosed] = useState(false);
  useEffect(() => { if (!isPrerender()) setHeroClosed(!isAnyStoreOpenNow()); }, []);

  // LCP hero-kép preload (CSAK a főoldalon, és CSAK EGY kép). A prerender a
  // linket a statikus <head>-be süti → a böngésző már a HTML-parse alatt
  // indítja a letöltést.
  //
  // Miért AVIF és nem WebP: a <picture> első <source>-a az AVIF, tehát egy
  // AVIF-képes böngésző azt tölti le – ha a WebP-t preloadolnánk, az sima
  // felesleges letöltés lenne. A `type` kapuzás miatt AVIF-et nem ismerő
  // böngésző IGNORÁLJA ezt a linket (és a <picture> WebP/JPEG ágán megy
  // tovább), tehát dupla letöltés egyik ágon sincs.
  // (DOM-injektálás a SEOHead canonical-mintája szerint – a Helmet <link>
  // renderelése React 19 alatt nem megbízható.)
  useEffect(() => {
    if (document.querySelector('link[rel="preload"][href="/fooldalnorbikep.avif"]')) return undefined;
    const l = document.createElement('link');
    l.rel = 'preload'; l.as = 'image'; l.href = '/fooldalnorbikep.avif';
    l.type = 'image/avif'; l.fetchPriority = 'high';
    document.head.appendChild(l);
    return () => { try { document.head.removeChild(l); } catch {} };
  }, []);

  // Fetch reviews and stores – a reviews munkamenet-cache-elt (a StoresPage /
  // landing-oldalak ugyanabból a cache-ből kapják, nincs ismételt kérés).
  useEffect(() => {
    // AbortController a sima fetch-ekre (route-váltáskor megszakítjuk); a
    // cache-elt reviews-t NEM abortáljuk (a promise-t más oldal is használja),
    // ott az `alive` flag védi a setState-et unmount után.
    const ac = new AbortController();
    let alive = true;
    const fetchData = async () => {
      try {
        // A korábbi /api/stores hívás TÖRÖLVE (K15): a végpont nem létezik a
        // backenden (404 minden főoldal-betöltésnél), a `stores` state örökre
        // üres maradt, és minden felhasználási hely amúgy is a
        // constants/stores.js fallbackre esett – az az igazságforrás.
        const [reviewsData, summaryRes] = await Promise.all([
          fetchJsonCached(`${API_BASE}/api/reviews`).catch(() => null),
          fetch(`${API_BASE}/api/reviews/summary`, { signal: ac.signal })
        ]);
        if (!alive) return;
        if (Array.isArray(reviewsData)) setReviews(reviewsData);
        if (summaryRes.ok) {
          const s = await summaryRes.json();
          // csak akkor használjuk, ha a Places konfigurált ÉS van valós adat
          if (alive && s.configured && (s.belvaros || s.tesco)) setSummary(s);
        }
      } catch (err) {
        // silent fail – statikus fallback értékek maradnak (abort is ide fut)
      }
    };
    fetchData();
    return () => { alive = false; ac.abort(); };
  }, [API_BASE]);

  // A VIP feliratkozás LOGIKÁJA a VipScratch komponensbe költözött (kaparós
  // sorsjegy: /api/vip/scratch → /api/vip/subscribe). A korábbi, közvetlen
  // /api/newsletter/subscribe hívás innen TÖRÖLVE.

  // Store addresses for Google Maps
  const storeAddresses = {
    tesco: {
      name: 'Tesco üzlet',
      address: '5900 Orosháza, Vásárhelyi út 23/A',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1800!2d20.6491554!3d46.5480352!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47446fb4d94a9fd1%3A0x79b626132856c685!2sNorbiPhone%20Orosh%C3%A1za%20Tesco!5e0!3m2!1shu!2shu!4v1748000000000!5m2!1shu!2shu'
    },
    belvaros: {
      name: 'Belvárosi üzlet',
      address: '5900 Orosháza, Rákóczi út 16',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1800!2d20.6657352!3d46.5576868!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47443f28d0e53c9b%3A0x93d6a6af33a6aa32!2sNorbiPhone%20Orosh%C3%A1za%20Belv%C3%A1ros!5e0!3m2!1shu!2shu!4v1748000000000!5m2!1shu!2shu'
    }
  };

  // `event`: a mérési esemény utótagja (trackCtaClick) – így GA4-ben látszik,
  // melyik szolgáltatás húzza a forgalmat. Az útvonal NEVE nem elég: a
  // kattintást a React Router kezeli, oldalbetöltés nélkül.
  const services = [
    {
      icon: Wrench,
      title: 'Szerviz',
      description: 'Megmondjuk, mi a baj, megcsináljuk, visszaadjuk.',
      link: '/szerviz/',
      linkText: 'Részletek',
      event: 'szerviz'
    },
    {
      icon: Shield,
      title: 'Hidrogél fólia',
      description: 'Felrakjuk, és szinte észre sem veszed – csak azt, hogy védve van.',
      link: '/hidrogel/',
      linkText: 'Típusok és árak',
      event: 'hidrogel'
    },
    {
      icon: Smartphone,
      title: 'Telefonok',
      description: 'Bevizsgált, garanciális készülékek – és a régidet be is számítjuk.',
      link: '/telefonok/',
      // Nem „Készlet megtekintése”: azt a szöveget a hero elsődleges CTA-ja
      // viseli – két azonos feliratú gomb egy oldalon összemosódik.
      linkText: 'Készlet és árak',
      event: 'telefonok'
    },
    {
      icon: RefreshCw,
      title: 'Felvásárlás',
      description: 'Hozd be, megnézzük, és ha megegyezünk – azonnali készpénzes kifizetés.',
      link: '/felvasarlas/',
      linkText: 'Mennyit ér a telefonom?',
      event: 'felvasarlas'
    }
  ];

  // Kizárólag VALÓS (backend/Google) értékelések – nincs hardcode fallback.
  // Ha nincs adat, a review-szekció egyszerűen nem jelenik meg.
  // useMemo: gépeléskor (VIP-űrlap state) nem épül újra, és a ReviewCarousel
  // prop-referenciája stabil marad (nem reconcile-ol feleslegesen).
  const displayReviews = useMemo(() => {
    // 1) Deduplikáció: ugyanaz a vélemény (szerző + szövegkezdet) egyszer se
    //    ismétlődjön (a duplikált „Adam Kozák” ellen).
    const seen = new Set();
    const unique = [];
    for (const r of reviews) {
      const key = `${(r.name || '').trim().toLowerCase()}|${(r.text || '').trim().slice(0, 40).toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(r);
    }
    // 2) Üzlet-kiegyensúlyozás: ha MINDKÉT üzletnek van véleménye, felváltva
    //    jelennek meg (kb. fele-fele). Ha csak egy üzleté van, marad sorrendben.
    const norm = (s) => String(s || '').toLowerCase();
    const belv = unique.filter((r) => norm(r.store).includes('belv'));
    const tesco = unique.filter((r) => norm(r.store).includes('tesco'));
    let ordered = unique;
    if (belv.length && tesco.length) {
      ordered = [];
      const max = Math.max(belv.length, tesco.length);
      for (let i = 0; i < max; i++) {
        if (belv[i]) ordered.push(belv[i]);
        if (tesco[i]) ordered.push(tesco[i]);
      }
    }
    // 3) Megjelenítési címke (az üzlet-címke minden kártyán megmarad)
    return ordered.map((r) => ({ ...r, store: STORE_LABELS[r.store] || r.store }));
  }, [reviews]);

  // A vélemény-blokk láthatóság-mérése. IntersectionObserver, mert a puszta
  // DOM-jelenlét nem jelenti, hogy a látogató LÁTTA is – a blokk a lap 19%-ánál
  // van, tehát ez valódi szűrő. Küszöb: a blokk 30%-a a képernyőn.
  const reviewsRef = useRef(null);
  useEffect(() => {
    const el = reviewsRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        trackReviewsVisible('homepage');
        io.disconnect();          // oldalanként egyszer elég
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [displayReviews.length]);

  const handleReviewsExpand = useCallback(() => trackReviewsExpand('homepage'), []);

  // Megjelenített összesítő: valós Places-adat, statikus fallbackkel.
  // A két üzlet KÜLÖN – a fő szám a belvárosi profilé, a darabszám a kettő
  // ÖSSZEGE (nem átlag!), a bontás soronként külön is megjelenik.
  const ratingDisplay = summary?.belvaros?.rating ?? 4.9;
  const totalReviewCount = summary
    ? (summary.belvaros?.count || 0) + (summary.tesco?.count || 0)
    : null;

  const trustPoints = [
    { title: 'Ingyenes bevizsgálás', description: 'Díjmentesen felmérjük a hibát', icon: 'search' },
    { title: '6 hónap garancia', description: 'Minden javításra', icon: 'shield' },
    { title: 'Helyi bolt', description: 'Nem webshop. Személyesen, Orosházán.', icon: 'badge' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <SEOHead
        title="Telefonos bolt és mobilszerviz Orosházán | NorbiPhone"
        description="NorbiPhone – telefonos bolt és mobilszerviz Orosházán: iPhone, Samsung, Xiaomi, Honor és Huawei javítás, használt telefonok, hidrogél fólia. Ingyenes bevizsgálás, 6 hó garancia."
        url="https://norbiphone.hu/"
      />
      <section className="pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-32 lg:pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div>
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-yellow-50 border border-yellow-200 rounded-full mb-4" data-testid="hero-trust-badge">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-gray-700">4,9★ · 500+ Google-értékelés</span>
              </div>

              <h1 className="text-[2rem] sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.15] sm:leading-tight mb-3" data-testid="hero-title">
                Telefon probléma? <span className="text-brand">Orosházán megoldjuk.</span>
              </h1>
              {/* Egy mondat, a fő kulcsszóval. A korábbi felsorolás 320 px-en 5 sor
                  volt, a mögötte álló „Ingyenes bevizsgálás · 6 hó garancia · 4,9★”
                  sor pedig szó szerint megismételte a fenti badge-et ÉS az alatta
                  következő SocialProofStrip-et – így a CTA a hajtás alá csúszott. */}
              <p className="text-base sm:text-xl text-gray-600 mb-6 leading-relaxed" data-testid="hero-subtitle">
                <b className="text-ink">Telefonos bolt és mobilszerviz Orosházán</b> – javítás,
                felvásárlás, fólia és bevizsgált használt telefonok.
              </p>
              {/* CTA-hierarchia: elsődleges = készlet, másodlagos = hívás.
                  Nyitvatartáson kívül (a hét kb. 60%-a) a „Hívás” halott gomb
                  lenne, ezért ott visszahívás áll a helyén – ugyanaz a minta,
                  mint a MobileCTABar-ban és a /felvasarlas heróban. */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/telefonok/"
                  onClick={() => trackCtaClick('fooldal_hero_keszlet')}
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 sm:px-7 py-4 min-h-[52px] bg-brand text-white font-semibold text-base sm:text-lg rounded-full hover:bg-brand-hover transition-colors shadow-lg"
                  data-testid="hero-cta-primary"
                >
                  <Smartphone size={20} />
                  Készlet megtekintése
                </Link>
                {heroClosed ? (
                  <Link
                    to="/szerviz/#visszahivas"
                    onClick={() => trackCtaClick('fooldal_hero_visszahivas')}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 sm:px-7 py-4 min-h-[52px] bg-white text-ink font-semibold text-base sm:text-lg rounded-full border-2 border-ink hover:bg-gray-50 transition-colors"
                    data-testid="hero-cta-callback"
                  >
                    <Clock size={20} />
                    Visszahívást kérek
                  </Link>
                ) : (
                  <a
                    href={`tel:${PRIMARY_PHONE}`}
                    onClick={() => trackCallClick('homepage_hero')}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 sm:px-7 py-4 min-h-[52px] bg-white text-ink font-semibold text-base sm:text-lg rounded-full border-2 border-ink hover:bg-gray-50 transition-colors"
                    data-testid="hero-cta-call"
                  >
                    <Phone size={20} />
                    Hívás most
                  </a>
                )}
              </div>
              {/* Megerősítés a CTA UTÁN – döntés után nyugtat, nem tolja lejjebb a gombot. */}
              <p className="text-sm font-semibold text-gray-500 mt-3">
                Ingyenes bevizsgálás · 6 hónap garancia minden javításra
              </p>
              {/* K13: „hol vagytok?” az ELSŐ képernyőn. 320 px-en az üzletekre
                  mutató link a hajtás alá csúszott, és a hero csak annyit mondott,
                  hogy „Orosházán” – konkrét hely nélkül. Az 5 másodperces teszten
                  ez volt az egyetlen megválaszolatlan kérdés a háromból.
                  Szöveg, nem újabb gomb: a hero gombsora nem hígulhat. */}
              <p className="text-sm text-gray-500 mt-1" data-testid="hero-locations">
                Két üzlet Orosházán — Tesco üzletsor és Belváros
              </p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
                <a
                  href={MESSENGER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackMessengerClick('homepage_hero')}
                  className="inline-flex items-center gap-1.5 text-gray-500 font-semibold text-sm hover:text-ink transition-colors"
                  data-testid="hero-cta-messenger"
                >
                  <MessageCircle size={15} />
                  Messenger
                </a>
                <Link
                  to="/uzletek/"
                  onClick={() => trackCtaClick('fooldal_hero_uzletek')}
                  className="inline-flex items-center gap-1.5 text-gray-500 font-semibold text-sm hover:text-ink transition-colors"
                  data-testid="hero-cta-stores"
                >
                  <MapPin size={15} />
                  Üzleteink és nyitvatartás
                  <ChevronRight size={15} />
                </Link>
              </div>
            </div>

            {/* Right - Image */}
            <div
              className="relative"
            >
              <div className="aspect-square max-w-lg mx-auto">
                <picture>
                  <source srcSet="/fooldalnorbikep.avif" type="image/avif" />
                  <source srcSet="/fooldalnorbikep.webp" type="image/webp" />
                  <img
                    src="/fooldalnorbikep.jpg"
                    alt="Papp Norbert a NorbiPhone orosházi üzletében, kezében egy iPaddel és egy iPhone-nal, mögötte a tokválaszték fala"
                    className="w-full h-full object-cover rounded-3xl shadow-2xl"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="1024"
                    height="1024"
                    data-testid="hero-image"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip – azonnali bizalmi jelek a hero alatt */}
      <SocialProofStrip />

      {/* Bizalmi mondat a sáv alatt. SZÁNDÉKOSAN nem a SocialProofStrip
          négy elemének egyike: az egy ikonos, rövid címkékre készült rács –
          ez a mondat hosszabb, és pont attól hat, hogy önmagában áll. */}
      <div className="bg-white border-b border-gray-100" data-testid="home-trust-stat">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <p className="text-center text-sm sm:text-base text-gray-700 leading-relaxed">
            <span className="font-bold text-ink">100 eladott telefonból 97-98 soha nem jön vissza.</span>{' '}
            Ez nem ígéret — ez a statisztikánk.
          </p>
        </div>
      </div>

      {/* Services Section – a négy bevételi útvonal. SZÁNDÉKOSAN közvetlenül a
          hero alatt: korábban az oldal 25%-ánál (3368 px) kezdődött, a VIP
          e-mail-űrlap MÖGÖTT. A kártyák CTA-ja gomb, nem szöveglink: ezek a
          legfontosabb belépők, korábban vizuálisan súlytalanok voltak. */}
      <section className="py-14 sm:py-20" data-testid="services-section" aria-labelledby="szolgaltatasok-cim">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* A szekciónak eddig NEM volt címsora: a H1 után rögtön a kártyák H3-jai
              jöttek (h1→h3 ugrás a képernyőolvasónak), és egy címsor nélküli
              blokkot az AI-összefoglalók sem tudnak mihez kötni. */}
          <div className="text-center mb-8 sm:mb-10">
            <h2 id="szolgaltatasok-cim" className="text-2xl sm:text-3xl font-bold text-ink mb-2">
              Mivel tudunk segíteni?
            </h2>
            <p className="text-gray-600">Négy dolgot csinálunk, mind a négyet helyben, Orosházán.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {services.map((service, index) => (
              <div
                key={service.title}
              >
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow h-full flex flex-col">
                  <div className="w-12 h-12 bg-brand/10 rounded-2xl flex items-center justify-center mb-4">
                    <service.icon className="text-brand-hover" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-ink mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-grow">{service.description}</p>
                  <Link
                    to={service.link}
                    onClick={() => trackCtaClick(`fooldal_szolgaltatas_${service.event}`)}
                    className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] px-4 py-3 bg-brand-light text-ink font-semibold rounded-xl border border-brand/30 hover:bg-brand hover:text-white hover:border-brand transition-colors"
                    data-testid={`service-link-${index}`}
                  >
                    {service.linkText}
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Google vélemények – az oldal EGYETLEN vélemény-blokkja ──────────
          Helye SZÁNDÉKOSAN közvetlenül a szolgáltatások alatt marad (mérve
          2 536 px / a lap 19%-a). Feljebb csak a szolgáltatás-kártyák elé
          lehetne tenni, ami a négy bevételi útvonal CTA-ját 1 549 → 2 515 px-re
          tolná, és egy OLDALRÓL KIVIVŐ (Google) CTA-t tenne a pénzútvonalak elé.

          Ami viszont változott: a blokk fejléce korábban 330 px-t vitt el az
          első véleménymondat előtt (Google-sor + 72 px-es szám + bontás + H2 +
          alcím). Most egyetlen tömör, élő sor. */}
      {displayReviews.length > 0 && (
        <section
          className="py-14 sm:py-16"
          data-testid="reviews-section"
          aria-labelledby="velemenyek-cim"
          ref={reviewsRef}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <h2 id="velemenyek-cim" className="text-2xl sm:text-3xl font-bold text-ink mb-3" data-testid="reviews-title">
                Mit mondanak rólunk
              </h2>

              {/* Élő átlag + élő darabszám EGY sorban. A `summary` a Google
                  Places-ből jön; ha nincs konfigurálva, a statikus fallback megy
                  ki – kitalált szám SOHA. */}
              <p className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-gray-600">
                <span className="inline-flex items-center gap-1" aria-hidden="true">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </span>
                <span className="text-xl font-bold text-ink tabular-nums" data-testid="reviews-rating">
                  {String(ratingDisplay).replace('.', ',')}
                </span>
                <span className="text-gray-600">
                  ·{' '}
                  <span data-testid="reviews-count">
                    {totalReviewCount ? `${totalReviewCount} értékelés` : '500+ értékelés'}
                  </span>
                  {' '}a Google-ön
                </span>
              </p>
              {summary && (
                <p className="text-xs text-gray-600 mt-1" data-testid="reviews-store-breakdown">
                  {summary.belvaros && (
                    <>Belváros: {String(summary.belvaros.rating).replace('.', ',')}★ ({summary.belvaros.count})</>
                  )}
                  {summary.belvaros && summary.tesco && ' · '}
                  {summary.tesco && (
                    <>Tesco: {String(summary.tesco.rating).replace('.', ',')}★ ({summary.tesco.count})</>
                  )}
                </p>
              )}
            </div>

            {/* Ugyanaz a komponens, mint eddig – csak a megjelenített darabszám
                lett 2 (perPage prop), hogy két teljes vélemény férjen ki
                mobilon is, görgetés nélkül. */}
            <ReviewCarousel
              reviews={displayReviews}
              perPage={2}
              mobilePerPage={2}
              autoPlayInterval={8000}
              onNavigate={handleReviewsExpand}
            />

            {/* EGY CTA. Korábban két gomb állt itt, és mindkettő a VÉLEMÉNYÍRÓ
                mélylinkre (g.page/.../review) mutatott – vásárlás előtt álló
                látogatónak irreleváns, ráadásul „az összes vélemény” helyett az
                írás-párbeszédet nyitotta. Most a Cégprofil olvasható oldalára megy. */}
            <div className="text-center mt-8">
              <a
                href={STORES.belvaros.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackGoogleReviewsOpen('homepage_osszes_velemeny')}
                aria-label="Az összes Google-vélemény megtekintése – megnyitás új lapon"
                data-testid="reviews-cta-all"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 min-h-[48px] rounded-full bg-white border-2 border-brand text-ink font-semibold hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 transition-colors"
              >
                <Star size={18} className="text-yellow-500 fill-yellow-500" aria-hidden="true" />
                Az összes Google-vélemény megtekintése
                <ExternalLink size={16} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Valódi üzletfotók + 360 a főoldal első harmadában. A vélemények UTÁN
          és az „Így dolgozunk” ELŐTT: mérve a lap ~27%-a, és így egyetlen
          bevételi útvonal CTA-ja sem csúszik lejjebb. */}
      <StoreShowcase className="bg-gray-50" />

      {/* „Így dolgozunk” – 3 lépés teaser. A RÉSZLETES leírás a Miért NorbiPhone
          oldalon van (nem ismételjük); itt csak a lényeg + link. */}
      <section className="py-16 bg-white" data-testid="home-how-we-work">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-ink mb-3">Így dolgozunk</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Minden használt készüléket a saját szervizünkben vizsgálunk be, mielőtt eladjuk.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Search, title: 'Bevizsgáljuk', desc: `${ELLENORZES_DB} ponton, az akkutól a vízkárnyomokig — a saját szervizünkben.` },
              { icon: Camera, title: 'Valódi fotó + IMEI', desc: 'A képeken a konkrét készülék látható, az egyedi IMEI-vel — nem katalógusfotó.' },
              { icon: Store,  title: 'Kipróbálod', desc: 'Bejössz az orosházi üzletbe, kézbe veszed, és csak utána fizetsz.' },
            ].map((s) => (
              <div key={s.title} className="bg-gray-50 rounded-2xl p-7 border border-gray-100 text-center">
                <div className="w-14 h-14 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <s.icon className="text-brand-hover" size={26} />
                </div>
                <h3 className="text-lg font-bold text-ink mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/miert-norbiphone/" className="inline-flex items-center gap-1.5 text-brand font-semibold hover:gap-2.5 transition-all" data-testid="home-how-we-work-link">
              Nézd meg, hogyan vizsgálunk be <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Business Stats */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-center text-2xl font-bold text-ink mb-8">Számokban rólunk</h2>
          {/* 6 elem, 3 hasáb: az „Ingyenes bevizsgálás” korábban külön szekcióban
              („Amit tudni érdemes rólunk”) állt, ami szóról szóra ugyanazt a három
              állítást ismételte, mint ez a rács és a SocialProofStrip. A szekció
              törölve, az egyetlen NEM duplikált eleme ide került. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {[
              { val: '4,9', label: 'Google értékelés', sub: '500+ vélemény', icon: '⭐' },
              { val: '0 Ft', label: 'Bevizsgálás', sub: 'minden esetben ingyenes', icon: '🔍' },
              { val: '6 hó', label: 'Garancia', sub: 'minden javításra', icon: '🛡️' },
              { val: '2', label: 'Üzlet', sub: 'belváros & Tesco', icon: '📍' },
              { val: '6+', label: 'Éve működünk', sub: 'helyi bolt, Orosháza', icon: '🏪' },
              { val: 'Másnapra', label: 'Javítási idő', sub: 'gyors, helyszíni', icon: '⚡' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-2xl mb-1">{s.icon}</span>
                <p className="text-2xl font-bold text-ink leading-tight">{s.val}</p>
                <p className="text-xs font-semibold text-gray-700 mt-0.5">{s.label}</p>
                <p className="text-xs text-gray-500">{s.sub}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/miert-norbiphone/" className="inline-flex items-center gap-1.5 text-brand font-semibold text-sm hover:gap-2.5 transition-all">
              Miért NorbiPhone? <ChevronRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* „Nem webshop vagyunk” – helyi jelenlét, felvezeti a két üzletet */}
      <section className="py-16 bg-white" data-testid="home-not-webshop">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-gray-50 rounded-3xl p-8 sm:p-10 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-ink mb-3">Nem webshop vagyunk</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Nálunk nem előre utalsz egy idegennek. Bejössz Orosházán, megnézed, kipróbálod — és valódi emberekkel beszélsz.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Store, title: 'Két üzlet Orosházán', desc: 'Belvárosi üzlet és Tesco üzletsor — valódi pult, valódi készlet.' },
                { icon: BadgeCheck, title: 'Személyes kipróbálás', desc: 'Kézbe veszed és kipróbálod a készüléket, mielőtt fizetsz.' },
                { icon: Users, title: 'Helyi csapat', desc: 'Ugyanaz a csapat vesz fel, javít és ad el — helyben, számonkérhetően.' },
              ].map((s) => (
                <div key={s.title} className="text-center">
                  <div className="w-14 h-14 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <s.icon className="text-brand-hover" size={26} />
                  </div>
                  <h3 className="text-lg font-bold text-ink mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-20 bg-gray-50" data-testid="stores-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-ink mb-4" data-testid="stores-title">
              Üzleteink
            </h2>
            <p className="text-gray-600 text-lg">Két helyszínen várunk Orosházán</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Tesco Store */}
            <div
              className="bg-white rounded-2xl overflow-hidden shadow-lg"
            >
              <div className="aspect-video w-full">
                <MapFacade src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1800!2d20.6491554!3d46.5480352!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47446fb4d94a9fd1%3A0x79b626132856c685!2sNorbiPhone%20Orosh%C3%A1za%20Tesco!5e0!3m2!1shu!2shu!4v1748000000000!5m2!1shu!2shu" title="NorbiPhone Tesco" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-ink mb-3">NorbiPhone Orosháza Tesco Üzletsor</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin size={18} className="text-brand flex-shrink-0" />
                    <span>5900 Orosháza, Vásárhelyi út 23/A</span>
                  </div>
                  <div className="flex items-start gap-3 text-gray-600">
                    <Clock size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p>H-P: 9:00-19:00</p>
                      <p>Szo: 9:00-18:00</p>
                      <p>V: Zárva</p>
                    </div>
                  </div>
                </div>
                <a
                  href={`tel:${STORES.tesco.phone}`}
                  onClick={() => trackCallClick('homepage_store_tesco')}
                  className="inline-flex items-center justify-center gap-2 w-full py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                  data-testid="store-tesco-call"
                >
                  <Phone size={20} />
                  Hívás most
                </a>
              </div>
            </div>

            {/* Belváros Store */}
            <div
              className="bg-white rounded-2xl overflow-hidden shadow-lg"
            >
              <div className="aspect-video w-full">
                <MapFacade src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1800!2d20.6657352!3d46.5576868!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47443f28d0e53c9b%3A0x93d6a6af33a6aa32!2sNorbiPhone%20Orosh%C3%A1za%20Belv%C3%A1ros!5e0!3m2!1shu!2shu!4v1748000000000!5m2!1shu!2shu" title="NorbiPhone Belváros" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-ink mb-3">NorbiPhone Belvárosi Üzlet</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin size={18} className="text-brand flex-shrink-0" />
                    <span>5900 Orosháza, Rákóczi út 16</span>
                  </div>
                  <div className="flex items-start gap-3 text-gray-600">
                    <Clock size={18} className="text-brand flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p>H-P: 9:00-17:00</p>
                      <p className="text-orange-700">Ebédszünet: 12:00-13:00</p>
                      <p>Szo: 9:00-12:00</p>
                      <p>V: Zárva</p>
                    </div>
                  </div>
                </div>
                <a
                  href={`tel:${STORES.belvaros.phone}`}
                  onClick={() => trackCallClick('homepage_store_belvaros')}
                  className="inline-flex items-center justify-center gap-2 w-full py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                  data-testid="store-belvaros-call"
                >
                  <Phone size={20} />
                  Hívás most
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIP Club Section – KAPARÓS SORSJEGY.
          A korábbi „2 000 Ft kedvezmény minden szervizre” ígéret MEGSZŰNT: a
          2 000 Ft mostantól hat nyeremény egyike, 12% eséllyel. Garantált
          ígéretként hirdetni olyan ígéret lenne, amit az esetek 88%-ában nem
          tartunk be – a látogató a pultnál szembesülne vele.
          A saját feliratkozó űrlap is törölve: egy oldalon egy feliratkozási út
          lehet, különben a két hívás (newsletter/subscribe vs vip/subscribe)
          versengene ugyanazért az e-mail-címért. */}
      <section className="py-12 bg-gray-50" data-testid="vip-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div
            className="bg-white rounded-3xl p-8 sm:p-10 border-2 border-brand/30 shadow-lg"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-3" data-testid="vip-title">
                VIP Klub – iratkozz fel és nyerj
              </h2>
              <p className="text-gray-600 text-lg" data-testid="vip-subtitle">
                Kaparj le egy jegyet, és nyerj <span className="font-semibold text-ink">fóliát, tokot, kábelt vagy szervizkupont</span>.
                Minden jegy nyer, vásárolnod nem kell hozzá semmit.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <VipScratch variant="inline" />
            </div>
          </div>
        </div>
      </section>

      {/* EEAT – Miért bíznak bennünk? */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-ink">Miért bíznak bennünk Orosházán?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { emoji: '🏪', title: 'Valódi üzlet, személyes ügyintézés', desc: 'Nem webshop. Bejössz, megmutatjuk, mi a hiba, elmagyarázzuk, mi lesz – te döntesz.' },
              { emoji: '🔧', title: 'Több éves szerviz tapasztalat', desc: 'iPhone, Samsung, Xiaomi, Honor, Huawei – ezeket csináljuk nap mint nap, évek óta, itt Orosházán.' },
              { emoji: '🛡️', title: '6 hónap garancia minden javításra', desc: 'Nem csak elvégezzük a munkát – mögé is állunk. Ha gond van, visszajöhetsz.' },
              { emoji: '⭐', title: '500+ Google értékelés, 4,9 átlag', desc: 'Nem mi mondjuk, hogy jók vagyunk – ezt az ügyfeleinktől olvashatod el a Google-ön.' },
            ].map(({ emoji, title, desc }) => (
              <div key={title} className="text-center p-5 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="text-4xl mb-3">{emoji}</div>
                <h3 className="font-bold text-ink mb-2 text-sm leading-snug">{title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BeforeAfterSection />
      {/* TikTok-szekció IDEIGLENESEN ELREJTVE (2026-08-19, Norbi kérésére):
          a kártyák kitalált videó-blokkok voltak valódi tartalom nélkül.
          NE töröld – ha lesz valódi videótartalom, a kikommentezés
          visszavonásával aktiválható. Az import fent szándékosan marad. */}
      {false && <TikTokSection />}

      {/* Blog előzetes */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-ink">Hasznos cikkek mobiltelefon témában</h2>
            <Link to="/blog/" className="text-brand font-semibold text-sm hover:underline">Összes cikk →</Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { to: '/blog/iphone-kijelzocsere-ar/', title: 'Mennyibe kerül egy iPhone kijelzőcsere?', cat: 'Szerviz' },
              { to: '/blog/mikor-kell-akkumulatort-cseralni/', title: 'Mikor kell akkumulátort cserélni?', cat: 'Szerviz' },
              { to: '/blog/hasznalt-iphone-vasarlas-tippek/', title: 'Mire figyelj használt iPhone vásárlásnál?', cat: 'Vásárlás' },
            ].map(item => (
              <Link key={item.to} to={item.to} className="block p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow group">
                <span className="text-xs font-semibold text-brand mb-2 block">{item.cat}</span>
                <p className="text-sm font-semibold text-ink group-hover:text-brand transition-colors leading-snug">{item.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO szövegblokk */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-ink mb-4">Mobilszerviz Orosházán – iPhone, Samsung, Xiaomi, Honor, Huawei</h2>
          <p className="text-gray-600 leading-relaxed">
            A NorbiPhone Orosházán foglalkozik iPhone, Samsung, Xiaomi, Honor, Huawei és más mobiltelefonok javításával. Kijelzőcsere, akkumulátorcsere, hidrogél fólia, használt telefonok és készülék felvásárlás két üzletben, gyors ügyintézéssel és garanciával.
          </p>
        </div>
      </section>

      {/* ── ELTÁVOLÍTVA: rejtett, kulcsszavas linkblokk ──────────────────────
          Itt korábban öt `display:none` + `aria-hidden` link állt „crawl boost”
          céllal, kulcsszavas horgonyszöveggel. A Google ezt REJTETT LINKNEK
          tekinti (Spam Policies – Hidden text and links), és manuális
          intézkedést vonhat maga után; a látogatónak semmit nem adott.
          Ráadásul feleslegessé is vált: háromból három cél alias volt, ami a
          saját canonicalja miatt úgysem rangsorolhatott, a maradék kettő pedig
          látható linket kap a márka-szekciókból. Ha egy oldalt be kell járni,
          mutasson rá LÁTHATÓ link – ez a mostani szerkezet alapelve. */}
    </div>
  );
};

export default HomePage;
